import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, Banknote, Search, X, Building2 } from "lucide-react";
import { useBranch } from "@/contexts/BranchContext";
import { BranchSelect } from "@/components/BranchSelect";
import { BranchFilter } from "@/components/BranchFilter";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";

const EMPTY = {
  bank_name: "", account_holder: "", account_number: "", branch_id: "", branch_name: "", ifsc: "",
  branch: "", account_type: "Current", opening_balance: 0,
  is_active: true, notes: "",
};

export default function BankLedgerPage() {
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const { active: activeBranch } = useBranch();

  const params = useMemo(() => (filterBranch ? { branch_id: filterBranch } : {}), [filterBranch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/bank-ledgers", params);
  const load = reload;
  const openFormTab = useFormTab();

  const filtered = list.filter(b => {
    if (filterBranch && b.branch_id !== filterBranch) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [b.bank_name, b.account_number, b.ifsc, b.branch, b.account_holder]
      .some(s => (s || "").toLowerCase().includes(q));
  });

  const openCreate = () => openFormTab({
    path: "/masters/bank-ledger/new",
    label: "New Bank Ledger",
    icon: Plus,
    parent: "/masters/bank-ledger",
    onSaved: load,
    render: ({ close }) => (
      <BankLedgerForm initial={{ ...EMPTY, branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "" }} editing={null} onClose={close} />
    ),
  });
  const openEdit = (b) => openFormTab({
    path: `/masters/bank-ledger/edit/${b.id}`,
    label: `Edit · ${b.bank_name || "Bank Ledger"}`,
    parent: "/masters/bank-ledger",
    onSaved: load,
    render: ({ close }) => (
      <BankLedgerForm initial={{ ...EMPTY, ...b }} editing={b} onClose={close} />
    ),
  });

  async function remove(b) {
    if (!window.confirm(`Delete bank "${b.bank_name} - ${b.account_number}"?`)) return;
    try {
      await api.delete(`/bank-ledgers/${b.id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 max-w-[1500px] mx-auto">
      <Toaster richColors />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Banknote size={26} className="text-[#F97316]"/> Bank Ledger
          </h1>
          <div className="text-xs text-slate-500 mt-1">Company bank accounts used for posting vendor payments and reconciliation.</div>
        </div>
        <button onClick={openCreate} data-testid="bl-new-btn"
          className="inline-flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C]">
          <Plus size={14}/> New Bank
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="bl-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by bank name, account number, IFSC…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="bl-filter-branch" />
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
          {filtered.length} banks
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {["Bank Name", "A/C Holder", "Account Number", "IFSC", "Branch", "Type", "Opening Bal", "Status", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3 whitespace-nowrap">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-slate-400">No bank ledgers yet. Click <strong>New Bank</strong> to add one.</td></tr>
            )}
            {filtered.map(b => (
              <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="py-2 px-3 font-bold text-slate-900 inline-flex items-center gap-1.5"><Building2 size={11} className="text-[#F97316]"/> {b.bank_name}</td>
                <td className="py-2 px-3 text-slate-600">{b.account_holder || "—"}</td>
                <td className="py-2 px-3 tabular text-slate-700">{b.account_number}</td>
                <td className="py-2 px-3 tabular text-slate-600">{b.ifsc || "—"}</td>
                <td className="py-2 px-3 text-slate-600">{b.branch || "—"}</td>
                <td className="py-2 px-3 text-[10px] uppercase font-bold bg-slate-50 text-slate-600">{b.account_type}</td>
                <td className="py-2 px-3 text-right tabular text-slate-700">{formatINR(b.opening_balance || 0)}</td>
                <td className="py-2 px-3">
                  {b.is_active ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Active</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">Inactive</span>
                  )}
                </td>
                <td className="py-2 px-3 flex items-center gap-1.5">
                  <button onClick={() => openEdit(b)} data-testid={`bl-edit-${b.id}`}
                    className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={12}/></button>
                  <button onClick={() => remove(b)} data-testid={`bl-delete-${b.id}`}
                    className="text-rose-500 hover:text-rose-700 p-1"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
      </div>
    </div>
  );
}

function BankLedgerForm({ initial, editing, onClose }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!form.bank_name.trim()) { toast.error("Bank name is required"); return; }
    if (!form.account_number.trim()) { toast.error("Account number is required"); return; }
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    setBusy(true);
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
      if (editing) {
        await api.put(`/bank-ledgers/${editing.id}`, payload);
        toast.success("Bank ledger updated");
      } else {
        await api.post("/bank-ledgers", payload);
        toast.success("Bank ledger created");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
        <h2 className="font-display text-xl font-bold">{editing ? "Edit Bank Ledger" : "New Bank Ledger"}</h2>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="bl-save"
            className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#F97316] text-white hover:bg-[#EA580C] disabled:opacity-50">
            {busy ? "Saving…" : (editing ? "Update" : "Create")}
          </button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 ml-2" data-testid="bl-form-close"><X size={18}/></button>
        </div>
      </div>
      <div className="p-6 grid grid-cols-2 gap-4 max-w-[1400px] mx-auto">
        <Field label="Branch *">
          <BranchSelect value={form.branch_id} onChange={(id, name) => setForm({ ...form, branch_id: id, branch_name: name })} testid="bl-branch-select" />
        </Field>
        <Field label="Bank Name *">
          <input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })}
            data-testid="bl-bank-name" className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Account Holder">
          <input value={form.account_holder} onChange={e => setForm({ ...form, account_holder: e.target.value })}
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Account Number *">
          <input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })}
            data-testid="bl-account-number" className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="IFSC Code">
          <input value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value.toUpperCase() })}
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Branch">
          <input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })}
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Account Type">
          <select value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none">
            {["Current", "Savings", "OD", "CC"].map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Opening Balance">
          <input type="number" step="0.01" value={form.opening_balance} onChange={e => setForm({ ...form, opening_balance: e.target.value })}
            className="w-full text-xs border border-slate-300 px-2 py-1.5 text-right focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Status">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer pt-1">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}/>
            Active (available for payment posting)
          </label>
        </Field>
        <div className="col-span-2">
          <Field label="Notes">
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
